import { useEffect, useState } from "react";
import { Alert, Input, Select, Space, Tabs, Tooltip } from "antd";
import { Navigate, useNavigate, useParams } from "react-router";

import { MetadataDashboardTemplate } from "@/components/Templates/MetadataDashboardTemplate";
import { PortfolioPageShell } from "@/views/Portfolio/shared";

const DATASETS = {
  studies: {
    label: "ClinicalTrials.gov",
    type: "external-trials",
    selectionIdentifierColumn: "registry_study_id",
    idLink: undefined,
    searchFields: [
      { value: "brief_title", label: "Study title" },
      { value: "registry_study_id", label: "NCT ID" },
      { value: "primary_sponsor", label: "Sponsor" },
    ],
  },
  products: {
    label: "Drugs@FDA",
    type: "fda-products",
    selectionIdentifierColumn: "regulatory_product_id",
    idLink: undefined,
    searchFields: [
      { value: "brand_name", label: "Brand name" },
      { value: "generic_name", label: "Generic name" },
      { value: "application_number", label: "Application" },
      { value: "sponsor_name", label: "Sponsor" },
    ],
  },
  safety: {
    label: "FAERS",
    type: "faers-signals",
    selectionIdentifierColumn: "signal_id",
    idLink: undefined,
    searchFields: [
      { value: "product_name", label: "Reported product" },
      { value: "reaction_term", label: "Reaction" },
    ],
  },
} as const;

// Commercial feeds already integrated into Vivo's portfolio data (trials,
// sites, investigators, operational metrics) rather than loaded here as public
// bulk releases. Shown so the full set of integrated sources is visible, but
// there is nothing to browse on this screen, so they are not selectable.
const INTEGRATED_SOURCES = [
  { key: "citeline", label: "Citeline" },
  { key: "dqs", label: "IQVIA DQS" },
] as const;

const INTEGRATED_SOURCE_HINT =
  "Integrated as a portfolio data source — not part of the public evidence releases browsable here.";

type EvidenceDataset = keyof typeof DATASETS;

export default function ExternalEvidence() {
  const { dataset } = useParams<{ dataset: string }>();
  const navigate = useNavigate();
  const [searchField, setSearchField] = useState("");
  const [searchValue, setSearchValue] = useState("");

  const hasValidDataset = Boolean(dataset && dataset in DATASETS);
  const activeDataset = hasValidDataset
    ? (dataset as EvidenceDataset)
    : "studies";
  const config = DATASETS[activeDataset];
  const selectedSearchField = searchField || config.searchFields[0].value;

  useEffect(() => {
    setSearchField("");
    setSearchValue("");
  }, [activeDataset]);

  const presetFilters = searchValue
    ? {
        [selectedSearchField]: [
          { condition: "contains" as const, value: searchValue },
        ],
      }
    : undefined;

  if (!hasValidDataset) {
    return <Navigate to="/portfolio/data/external/studies" replace />;
  }

  return (
    <PortfolioPageShell
      title="External"
      tooltipText="Search public regulatory product and safety-report data."
    >
      <Tabs
        activeKey={activeDataset}
        onChange={(key) => navigate(`/portfolio/data/external/${key}`)}
        items={[
          ...Object.entries(DATASETS).map(([key, value]) => ({
            key,
            label: value.label,
          })),
          ...INTEGRATED_SOURCES.map((source) => ({
            key: source.key,
            disabled: true,
            label: (
              <Tooltip title={INTEGRATED_SOURCE_HINT}>
                <span>{source.label}</span>
              </Tooltip>
            ),
          })),
        ]}
      />

      <Space.Compact className="mb-4 w-full max-w-[680px]">
        <Select
          aria-label="Search field"
          className="w-[180px]"
          // DATASETS is `as const`, so searchFields is a readonly tuple; antd
          // Select wants a mutable option array.
          options={[...config.searchFields]}
          value={selectedSearchField}
          onChange={(value) => {
            setSearchField(value);
            setSearchValue("");
          }}
        />
        <Input.Search
          allowClear
          aria-label={`Search ${config.searchFields.find((field) => field.value === selectedSearchField)?.label}`}
          placeholder={`Search ${config.searchFields.find((field) => field.value === selectedSearchField)?.label.toLowerCase()}`}
          onSearch={(value) => setSearchValue(value.trim())}
        />
      </Space.Compact>

      {activeDataset === "safety" ? (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message="FAERS counts are reports, not incidence rates, and do not establish causality."
        />
      ) : null}

      <MetadataDashboardTemplate
        key={`${config.type}-${selectedSearchField}-${searchValue}`}
        type={config.type}
        constants={{ wikiLink: "", idLink: config.idLink }}
        selectionIdentifierColumn={config.selectionIdentifierColumn}
        presetFilters={presetFilters}
      />
    </PortfolioPageShell>
  );
}
